import { InMemoryCache } from "@apollo/client";
import { LocalForageWrapper, persistCache } from "apollo3-cache-persist";
import localforage from "localforage";

/** localforage store for the normalized Apollo cache (offline reads). */
const cacheStore = localforage.createInstance({ name: "thruhiker", storeName: "cache" });

export function createCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          publicFeed: offsetMergePolicy(),
          personalFeed: offsetMergePolicy(),
          explore: offsetMergePolicy(),
        },
      },
      // Nested, per-entity offset-paginated connections also need a merge policy so
      // `fetchMore` appends pages instead of replacing them (infinite scroll on the
      // user-feed and adventure-feed pages).
      User: {
        fields: {
          posts: offsetMergePolicy(),
        },
      },
      Adventure: {
        fields: {
          posts: offsetMergePolicy(),
        },
      },
    },
  });
}

/**
 * Restore the persisted cache from localforage and keep it in sync. Awaited
 * during bootstrap so the first render can already show cached data offline.
 */
export async function restoreCache(cache: InMemoryCache): Promise<void> {
  await persistCache({
    cache,
    storage: new LocalForageWrapper(cacheStore),
    maxSize: false,
  });
}

function offsetMergePolicy() {
  return {
    keyArgs: false as const,
    // Connections use `edges` (aligned to the legacy schema), offset-paginated.
    merge(
      existing: { edges: unknown[]; pagination?: unknown } | undefined,
      incoming: { edges: unknown[]; pagination?: unknown },
      { args }: { args: Record<string, unknown> | null },
    ) {
      const offset = (args?.offset as number) ?? 0;
      const edges = existing ? existing.edges.slice() : [];
      const incomingEdges = incoming.edges ?? [];
      for (let i = 0; i < incomingEdges.length; i++) edges[offset + i] = incomingEdges[i];
      return { ...incoming, edges };
    },
  };
}
